import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor, ReactiveFormsModule } from '@angular/forms';
import { NiIconComponent } from '../ni-icon/ni-icon.component';

@Component({
  selector: 'NiTextField',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NiIconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NiTextFieldComponent),
      multi: true
    }
  ],
  templateUrl: './ni-text-field.component.html',
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    :host input,
    :host input[type="text"],
    :host input[type="email"],
    :host input[type="url"],
    :host input[type="password"],
    :host input[type="number"],
    :host input[type="tel"] {
      outline: none !important;
      box-shadow: none !important;
      --tw-ring-offset-shadow: none !important;
      --tw-ring-shadow: none !important;
      --tw-ring-color: transparent !important;
    }
    :host input:focus,
    :host input[type="text"]:focus,
    :host input[type="email"]:focus,
    :host input[type="url"]:focus,
    :host input[type="password"]:focus,
    :host input[type="number"]:focus,
    :host input[type="tel"]:focus {
      outline: none !important;
      box-shadow: none !important;
      --tw-ring-offset-shadow: none !important;
      --tw-ring-shadow: none !important;
      --tw-ring-color: transparent !important;
    }
    :host input:focus-visible {
      outline: none !important;
      box-shadow: none !important;
    }
    :host input::-webkit-autofill,
    :host input::-webkit-autofill:hover,
    :host input::-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0px 1000px white inset !important;
      box-shadow: 0 0 0px 1000px white inset !important;
    }
  `]
})
export class NiTextFieldComponent implements ControlValueAccessor {
  @Input() id: string = `textfield-${Math.random().toString(36).substring(2, 9)}`;
  @Input() name: string = 'textfield';
  @Input() label: string = 'Textfield';
  @Input() placeholder: string = 'Enter text';
  @Input() type: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' = 'text';
  @Input() value: string = 'test';
  @Input() disabled: boolean = false;
  @Input() required: boolean = true;
  @Input() readonly: boolean = false;
  @Input() error: string = 'error';
  @Input() showError: boolean = false;
  @Input() helperText: string = '';
  @Input() description: string = '';
  @Input() maxLength?: number;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() variant: 'outlined' | 'filled' | 'underlined' = 'outlined';
  @Input() color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'basic' | 'link' | 'text' = 'secondary';
  @Input() leftIcon: string = '';
  @Input() rightIcon: string = '';
  @Input() clearable: boolean = false;
  @Input() rounded: boolean = false;
  @Input() fullWidth: boolean = true;
  @Input() padding?: number | { top?: number; bottom?: number; left?: number; right?: number };
  @Input() height?: number;
  @Input() borderRadius?: number;
  @Input() customLabelClasses: string = '';
  @Input() customInputClasses?: string;
  @Input() border: string = '0';
  @Output() valueChange = new EventEmitter<string>();
  @Output() focus = new EventEmitter<FocusEvent>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() leftIconClick = new EventEmitter<MouseEvent>();
  @Output() rightIconClick = new EventEmitter<MouseEvent>();
  focused: boolean = false;

  // ControlValueAccessor implementation
  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  onFocus(event?: FocusEvent): void {
    this.focused = true;
    if (event) {
      this.focus.emit(event);
    }
  }

  onBlur(event?: FocusEvent): void {
    this.focused = false;
    this.onTouched();
    if (event) {
      this.blur.emit(event);
    }
  }

  clearInput(): void {
    this.value = '';
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  getInputClasses(): string {
    const baseClasses = 'block w-full transition-colors duration-200 outline-none focus:outline-none ring-0 focus:ring-0';
    const sizeClasses = this.getSizeClasses();
    const paddingClasses = this.getPaddingClasses();
    const { borderClasses, bgClasses } = this.getVariantClasses();
    const roundedClasses = this.getRoundedClasses();
    
    return `${baseClasses} ${sizeClasses} ${paddingClasses} ${borderClasses} ${bgClasses} ${roundedClasses} ${this.customInputClasses ?? ''} ${this.border}`.trim();
  }

  private getSizeClasses(): string {
    if (this.customInputClasses) {
      return this.customInputClasses;
    }
    const sizeMap = {
      'sm': 'text-sm',
      'md': 'text-base',
      'lg': 'text-lg'
    };
    return sizeMap[this.size] ?? 'text-base';
  }

  private getPaddingClasses(): string {
    if (this.padding !== undefined) {
      return '';
    }
    
    const verticalPadding = {
      'sm': 'py-1.5',
      'md': 'py-2',
      'lg': 'py-2.5'
    }[this.size] ?? 'py-2';
    
    if (this.leftIcon && this.rightIcon) {
      return `${verticalPadding} pl-10 pr-10`;
    }
    if (this.leftIcon) {
      return `${verticalPadding} pl-10 pr-3`;
    }
    if (this.rightIcon || this.clearable) {
      return `${verticalPadding} pl-3 pr-10`;
    }
    return `${verticalPadding} px-3`;
  }

  private getVariantClasses(): { borderClasses: string; bgClasses: string } {
    const colorClasses = this.getColorClasses();
    
    switch (this.variant) {
      case 'outlined':
        return this.getOutlinedClasses(colorClasses);
      case 'filled':
        return this.getFilledClasses(colorClasses);
      case 'underlined':
        return this.getUnderlinedClasses(colorClasses);
      default:
        return { borderClasses: '', bgClasses: '' };
    }
  }

  private getOutlinedClasses(colorClasses: any): { borderClasses: string; bgClasses: string } {
    const borderWidth = this.border ?? 'border-2';
    
    if (this.showError && this.error) {
      const errorBorder = colorClasses.error.border.replace('!', '');
      return {
        borderClasses: `${borderWidth} ${colorClasses.error.border} focus:!${errorBorder} focus:ring-1 ${colorClasses.error.ring}`,
        bgClasses: 'bg-white'
      };
    }
    
    const currentBorder = this.focused ? colorClasses.focus.border : colorClasses.default.border;
    const focusBorder = colorClasses.focus.border.replace('!', '');
    return {
      borderClasses: `${borderWidth} ${currentBorder} focus:!${focusBorder} focus:ring-1 ${colorClasses.focus.ring}`,
      bgClasses: 'bg-white'
    };
  }

  private getFilledClasses(colorClasses: any): { borderClasses: string; bgClasses: string } {
    const borderWidth = this.border ?? 'border-2';
    const transparentBorders = 'border-t-transparent border-l-transparent border-r-transparent border-b-2';
    
    if (this.showError && this.error) {
      const errorBorder = colorClasses.error.border.replace('!', '');
      return {
        borderClasses: `${borderWidth} ${transparentBorders} ${colorClasses.error.border} focus:!${errorBorder}`,
        bgClasses: colorClasses.error.bg
      };
    }
    
    const currentBorder = this.focused ? colorClasses.focus.border : colorClasses.default.border;
    const focusBorder = colorClasses.focus.border.replace('!', '');
    const bg = this.focused ? colorClasses.focus.bg : colorClasses.default.bg;
    
    return {
      borderClasses: `${borderWidth} ${transparentBorders} ${currentBorder} focus:!${focusBorder}`,
      bgClasses: bg
    };
  }

  private getUnderlinedClasses(colorClasses: any): { borderClasses: string; bgClasses: string } {
    if (this.showError && this.error) {
      const errorBorder = colorClasses.error.border.replace('!', '');
      return {
        borderClasses: `border-b ${colorClasses.error.border} focus:!${errorBorder}`,
        bgClasses: 'bg-transparent'
      };
    }
    
    const currentBorder = this.focused ? colorClasses.focus.border : colorClasses.default.border;
    const focusBorder = colorClasses.focus.border.replace('!', '');
    return {
      borderClasses: `border-b ${currentBorder} focus:!${focusBorder}`,
      bgClasses: 'bg-transparent'
    };
  }

  private getRoundedClasses(): string {
    if (this.borderRadius !== undefined) {
      return '';
    }
    return this.rounded ? 'rounded-full' : 'rounded-md';
  }

  private getColorClasses(): any {
    const colorMap = this.buildColorMap();
    return colorMap[this.color] ?? colorMap['primary'];
  }

  private buildColorMap(): Record<string, any> {
    const colorMap: Record<string, any> = {};
    
    // Standard color configs
    colorMap['primary'] = this.createColorConfig('gray-300', 'primary-500', 'gray-100', 'primary-50');
    colorMap['secondary'] = this.createSecondaryColorConfig();
    colorMap['success'] = this.createColorConfig('secondary-500', 'success-500', 'gray-100', 'success-50');
    colorMap['error'] = this.createErrorColorConfig();
    colorMap['warning'] = this.createColorConfig('gray-300', 'warning-500', 'gray-100', 'warning-50');
    colorMap['basic'] = this.createColorConfig('gray-300', 'gray-500', 'gray-100', 'gray-50');
    colorMap['link'] = this.createColorConfig('transparent', 'primary-500', 'transparent', 'transparent');
    colorMap['text'] = this.createColorConfig('transparent', 'gray-500', 'transparent', 'gray-50');
    
    return colorMap;
  }

  private createColorConfig(
    defaultBorder: string,
    focusBorder: string,
    defaultBg: string,
    focusBg: string
  ): any {
    return {
      default: { border: `!border-${defaultBorder}`, bg: `bg-${defaultBg}` },
      focus: { border: `!border-${focusBorder}`, ring: `focus:ring-${focusBorder}`, bg: `bg-${focusBg}` },
      error: { border: '!border-danger-500', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' }
    };
  }

  private createSecondaryColorConfig(): any {
    const defaultBorder = this.border ?? '!border-secondary-500';
    return {
      default: { border: defaultBorder, bg: 'bg-secondary-50' },
      focus: { border: '!border-primary-600', ring: 'focus:ring-primary-500 ring-1', bg: 'bg-secondary-100', shadow: 'shadow-sm focus:shadow-md' },
      error: { border: '!border-danger-500', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' }
    };
  }

  private createErrorColorConfig(): any {
    return {
      default: { border: '!border-danger-300', bg: 'bg-danger-50' },
      focus: { border: '!border-danger-500', ring: 'focus:ring-danger-500', bg: 'bg-danger-100' },
      error: { border: '!border-danger-500', ring: 'focus:ring-danger-500', bg: 'bg-danger-50' }
    };
  }

  getLabelClasses(): string {
    const baseClasses = `block font-medium ${this.customLabelClasses ? this.customLabelClasses : 'text-gray-700 text-sm'}`;
    if (this.showError && this.error) {
      return `${baseClasses} text-red-500`;
    }
    return `${baseClasses} text-gray-700 ${this.customLabelClasses}`;
  }

  getRequiredClasses(): string {
    // Required asterisk is always red, but can be adjusted based on error state
    if (this.showError && this.error) {
      return 'text-red-600';
    }
    return 'text-red-500';
  }

  getDescriptionClasses(): string {
    return 'text-xs text-gray-500';
  }

  getIconClasses(): string {
    const colorName = this.extractColorNameFromClasses();
    return `text-${colorName}-500`;
  }

  getClearButtonClasses(): string {
    const colorName = this.extractColorNameFromClasses();
    return `text-${colorName}-400 hover:text-${colorName}-600 mr-1 transition-colors duration-200`;
  }

  private extractColorNameFromClasses(): string {
    const colorClasses = this.getColorClasses();
    const borderClass = colorClasses.default.border;
    const colorMatch = borderClass.match(/border-(\w+)-/);
    return colorMatch ? colorMatch[1] : 'gray';
  }

  getErrorClasses(): string {
    return 'text-xs text-red-500';
  }

  getHelperClasses(): string {
    return 'text-xs text-gray-500';
  }

  getInputStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    this.applyPaddingStyles(styles);
    this.applyHeightStyles(styles);
    this.applyBorderRadiusStyles(styles);

    return styles;
  }

  private applyPaddingStyles(styles: { [key: string]: string }): void {
    if (this.padding === undefined) {
      return;
    }

    if (typeof this.padding === 'number') {
      styles['padding'] = `${this.padding}px`;
      return;
    }

    this.applyDirectionalPadding(styles);
  }

  private applyDirectionalPadding(styles: { [key: string]: string }): void {
    if (this.padding && typeof this.padding === 'object') {
      if (this.padding.top !== undefined) {
        styles['padding-top'] = `${this.padding.top}px`;
      }
      if (this.padding.bottom !== undefined) {
        styles['padding-bottom'] = `${this.padding.bottom}px`;
      }
      if (this.padding.left !== undefined) {
        styles['padding-left'] = `${this.padding.left}px`;
      }
      if (this.padding.right !== undefined) {
        styles['padding-right'] = `${this.padding.right}px`;
      }
    }
  }

  private applyHeightStyles(styles: { [key: string]: string }): void {
    if (this.height !== undefined) {
      styles['height'] = `${this.height}px`;
    }
  }

  private applyBorderRadiusStyles(styles: { [key: string]: string }): void {
    if (this.borderRadius !== undefined) {
      styles['border-radius'] = `${this.borderRadius}px`;
    }
  }
}